/**
 * AddFieldPicker — BottomSheet field picker for the Binding Editor (PIM Wave
 * P1 Task 8, design notes Surface 4). Search + type-filter chips over the
 * FULL field catalogue.
 *
 * Search choice (brief §6 — "pick one, state it"): CLIENT-SIDE filter over
 * the already-drained `useAllAttributeFields()` array (name_ru/slug
 * substring match, mirrors `CategoryListPage`'s client-side tree search),
 * NOT the existing server-`q` infinite `useAttributeFields` query. The
 * parent (`BindingEditorPage`) already holds the full drained field array
 * for row-name lookups (`fieldsById`) and passes it straight through here —
 * reusing it for search avoids mounting a SECOND field-list query type
 * (infinite/keyset vs. full-drain) side by side and keeps a single source
 * of field metadata for this whole feature.
 *
 * Row states (brief §6): fields already bound DIRECTLY on this category
 * render checked + disabled ("already bound"); fields bound only on an
 * ancestor render an "inherited" hint but stay SELECTABLE (selecting them
 * is still a plain create — see below); everything else is a plain
 * selectable row. Archived fields are excluded by default (brief §7).
 *
 * Selecting a field POSTs IMMEDIATELY with `requirement: "optional"`
 * appended at the end (brief §6 — this applies uniformly, including the
 * "inherited" rows: unlike `InheritedBindingsPanel`'s "Override here" — which
 * copies the ancestor binding's requirement/visibility_rule/weight verbatim
 * — a picker click never copies ancestor values, it always creates a fresh
 * default binding). The sheet stays open for multi-add; a duplicate 409
 * (`catalog_schema.binding.duplicate` — can still race if the same field was
 * just bound from another surface) surfaces as a specific toast rather than
 * the generic error copy.
 */
import { useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Check, Plus } from "lucide-react";

import { BottomSheet, Button, GPill } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { ApiError } from "@/lib/api-client";
import { useIsDesktop } from "@/features/chat/useIsDesktop";
import { AdminStates, FilterToolbar } from "@/features/admin/_shared";
import { FIELD_TYPE_META, fieldTypeMeta } from "@/features/admin/attribute-fields/lib/fieldTypeMeta";
import type { AttributeField } from "@/features/admin/attribute-fields/api";
import { useCreateCategoryBinding } from "@/features/admin/category-bindings/hooks";

const ICON_BUTTON_CLASS =
  "grid h-8 w-8 shrink-0 place-items-center rounded-full text-[var(--g-secondary)]";

export interface AddFieldPickerProps {
  open: boolean;
  onClose: () => void;
  categoryId: string;
  fields: AttributeField[];
  isLoading: boolean;
  isError: boolean;
  directFieldIds: ReadonlySet<string>;
  inheritedFieldIds: ReadonlySet<string>;
  nextSortOrder: number;
}

export function AddFieldPicker({
  open,
  onClose,
  categoryId,
  fields,
  isLoading,
  isError,
  directFieldIds,
  inheritedFieldIds,
  nextSortOrder,
}: AddFieldPickerProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const isDesktop = useIsDesktop();

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [pendingFieldId, setPendingFieldId] = useState<string | null>(null);
  const createMutation = useCreateCategoryBinding();

  const q = search.trim().toLowerCase();
  const candidates = useMemo(
    () =>
      fields
        .filter((f) => !f.is_archived)
        .filter((f) => (typeFilter ? f.field_type === typeFilter : true))
        .filter(
          (f) =>
            q === "" ||
            f.name_ru.toLowerCase().includes(q) ||
            f.slug.toLowerCase().includes(q),
        )
        .slice()
        .sort((a, b) => a.name_ru.localeCompare(b.name_ru, "ru")),
    [fields, typeFilter, q],
  );

  const handleSelect = (field: AttributeField): void => {
    if (directFieldIds.has(field.id) || pendingFieldId !== null) return;
    setPendingFieldId(field.id);
    createMutation.mutate(
      {
        categoryId,
        body: { field_id: field.id, requirement: "optional", sort_order: nextSortOrder },
      },
      {
        onSuccess: () => toast.success(t("pim.bindings.picker.addSuccess", { name: field.name_ru })),
        onError: (err) => {
          if (err instanceof ApiError && err.code === "catalog_schema.binding.duplicate") {
            toast.error(t("pim.bindings.picker.duplicate"));
          } else {
            toast.error(t("error.generic"));
          }
        },
        onSettled: () => setPendingFieldId(null),
      },
    );
  };

  return (
    <BottomSheet open={open} onClose={onClose} title={t("pim.bindings.picker.title")} desktop={isDesktop}>
      <div className="flex flex-col gap-3">
        <FilterToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={t("pim.bindings.picker.searchPlaceholder")}
          searchAriaLabel={t("pim.bindings.picker.searchAriaLabel")}
          chips={FIELD_TYPE_META.map((meta) => ({ id: meta.value, label: t(meta.labelKey) }))}
          activeChipId={typeFilter}
          onChipChange={setTypeFilter}
          allChipLabel={t("pim.fields.filter.allTypes")}
          testid="pim-bindings-picker-filter"
        />

        <AdminStates
          isLoading={isLoading}
          isError={isError}
          isEmpty={!isLoading && !isError && candidates.length === 0}
          loadingLabel={t("loading")}
          emptyLabel={t("pim.bindings.picker.empty")}
          errorLabel={t("error.loadFailed")}
          testid="pim-bindings-picker"
        >
          <ul className="flex max-h-[60vh] flex-col gap-2 overflow-y-auto" data-testid="pim-bindings-picker-list">
            {candidates.map((field) => {
              const alreadyDirect = directFieldIds.has(field.id);
              const isInherited = !alreadyDirect && inheritedFieldIds.has(field.id);
              const typeMeta = fieldTypeMeta(field.field_type);
              const isPending = pendingFieldId === field.id && createMutation.isPending;
              return (
                <li key={field.id}>
                  <button
                    type="button"
                    data-testid={`pim-bindings-picker-row-${field.id}`}
                    disabled={alreadyDirect || isPending}
                    onClick={() => handleSelect(field)}
                    aria-pressed={alreadyDirect}
                    className="flex min-h-[44px] w-full items-center gap-2 rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] p-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    <span className={ICON_BUTTON_CLASS}>
                      {alreadyDirect ? (
                        <Check size={16} strokeWidth={1.75} aria-hidden />
                      ) : (
                        <Plus size={16} strokeWidth={1.75} aria-hidden />
                      )}
                    </span>
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate text-sm font-medium">{field.name_ru}</span>
                        {typeMeta ? (
                          <GPill tone="neutral" size="sm">
                            {t(typeMeta.labelKey)}
                          </GPill>
                        ) : null}
                        {alreadyDirect ? (
                          <GPill tone="primary" size="sm">
                            {t("pim.bindings.picker.alreadyBound")}
                          </GPill>
                        ) : isInherited ? (
                          <GPill tone="neutral" size="sm">
                            {t("pim.bindings.picker.inheritedHint")}
                          </GPill>
                        ) : null}
                      </div>
                      <span className="truncate font-mono text-xs text-[var(--g-secondary)]">
                        {field.slug}
                      </span>
                    </div>
                    {isPending ? (
                      <span className="g-spin size-4 shrink-0 rounded-full border-2 border-current border-t-transparent" aria-hidden />
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </AdminStates>

        <div className="flex justify-end">
          <Button type="button" variant="ghost" size="sm" onClick={onClose} className="min-h-[44px]">
            {t("common.cancel")}
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
}

export default AddFieldPicker;
